import { getTimeFormat } from '@/shared/lib/time-format';
/**
 * Date / datetime formatters used by report cell rendering and other
 * tables that need to display backend-supplied timestamps.
 *
 * Mirrors Flutter `scDateFormat` / `scDateTimeFormat` from
 * `scrips_core/lib/utils/utils.dart`:
 *
 *   scDateFormat       → 'MMM dd, yyyy'        e.g. "Apr 21, 2026"
 *   scDateTimeFormat   → 'MMM dd, yyyy h:mma'  e.g. "Apr 21, 2026, 10:00 AM"
 *
 * Differences from Flutter:
 *
 * - React uses `toLocaleDateString` so users in different locales see
 *   the format they expect; the `en-US` fallback below matches the
 *   Flutter wire output and keeps tests deterministic.
 * - Flutter normalises the AM/PM marker to a lowercase "a" / "p". We
 *   keep the standard "AM" / "PM" — closer to system convention and
 *   less surprising in a web context.
 *
 * Inputs accept anything the backend has historically sent: an ISO
 * string, a Date instance, a numeric epoch (ms), or null/undefined.
 * Invalid inputs return an empty string so callers can fall back to
 * a placeholder.
 */

type DateInput = string | number | Date | null | undefined;

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
};

/** Built per call: the clock follows the viewer's time-format preference (24-hour by default). */
function datetimeOptions(): Intl.DateTimeFormatOptions {
  return {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: getTimeFormat() === '24h' ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: getTimeFormat() === '12h',
  };
}

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: DateInput, locale = 'en-US'): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(locale, DATE_OPTIONS) : '';
}

export function formatDateTime(value: DateInput, locale = 'en-US'): string {
  const d = toDate(value);
  return d ? d.toLocaleString(locale, datetimeOptions()) : '';
}

/**
 * "September 18, 2026" — the long day the calendar CHROME reads out: the
 * scheduling sub-toolbar's date trigger and, since PROD-2408, the Technician
 * Worklist header's. It lives here rather than beside either of them so the
 * two surfaces cannot drift into two spellings of the same day.
 *
 * Note the locale difference from `formatDate` above, and it is deliberate:
 * this one follows the USER's locale instead of defaulting to `en-US`. These
 * are toolbar labels a person reads, not wire-shaped table cells matching the
 * Flutter output — and it is the behaviour the sub-toolbar has always had.
 */
const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: '2-digit',
  year: 'numeric',
};

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString(undefined, LONG_DATE_OPTIONS);
}

/**
 * Format a Date / ISO string as the no-zone datetime the .NET model
 * binder expects: `YYYY-MM-DDTHH:mm:ss.sss` (no `Z`, no `+04:00`).
 *
 * Reads local clock components so the wire value matches the user's
 * intended slot regardless of system timezone. Returns `''` for
 * unparseable inputs so callers can fail loud rather than ship garbage.
 *
 * Replaces the older ad-hoc `iso.replace(/Z$/, '')` pattern — string
 * slicing silently mishandles offsets like `+04:00` and already-stripped
 * strings; this formatter is unambiguous on any input.
 */
export function formatLocalDatetimeForWire(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `.${pad(d.getMilliseconds(), 3)}`
  );
}

/**
 * Format a CALENDAR DAY for the wire: `YYYY-MM-DDT00:00:00.000` (no `Z`).
 *
 * Sibling of `formatLocalDatetimeForWire`, and the distinction matters
 * (PROD-1778). Use that one for an INSTANT — an appointment start, an audit
 * stamp, a vitals reading time. Use this one for a field whose meaning is a
 * DAY: license expiration, insurance eligibility start / expiry, a report's
 * date filter. Fields whose UI is an `<input type="date">` or a day-granularity
 * calendar are days, not instants.
 *
 * Why they can't be the same function: normalising a day through a timezone can
 * MOVE it. A value like `2026-12-31T00:00:00.000Z` (what
 * `new Date('2026-12-31').toISOString()` produces — `new Date('YYYY-MM-DD')`
 * parses as UTC midnight) becomes 2026-12-30 for anyone west of UTC if you
 * re-derive local components from the instant. So for string input this reads
 * the day TEXTUALLY and never parses it — the same rationale as `isoToDateInput`
 * in `practice-edit-merge.ts`. For a `Date` input, local calendar components are
 * correct by construction (that's the day the user clicked).
 *
 * Never `iso.slice(0, -1)`. Chopping the `Z` off a `toISOString()` result does
 * not restore local time — `toISOString()` already converted to UTC, so the
 * strip bakes the offset into the value the .NET binder then stores verbatim.
 *
 * Returns `''` for null / undefined / empty / unparseable input.
 *
 * ── INPUT CONTRACT (review of PR #778) ────────────────────────────────────
 * Callers are expected to pass a DAY: a `YYYY-MM-DD` string, a day-only
 * `YYYY-MM-DDT00:00:00…` string, or a `Date` at local midnight.
 *
 * Passing an INSTANT (`'2026-06-30T14:00:00'`) is a category error — the time
 * is **silently truncated to midnight**. That is deliberately not a throw:
 * this sits on live write paths, and a throw would turn a wrong-but-saveable
 * value into a failed save. Truncation is also the safe direction — it can
 * never move the day, which is the whole point of this function.
 *
 * But silent is how the bug class this function exists to fix survived three
 * sweeps, so in DEV a non-midnight time component warns. Use
 * `formatLocalDatetimeForWire` if you actually meant an instant.
 *
 * A branded `CalendarDay` type would enforce this at compile time, but it
 * cannot constrain the values that matter most — API responses arrive as
 * plain `string`, so they would need a cast at every boundary, which is where
 * the discipline would quietly break down.
 */
export function formatLocalDateOnlyForWire(value: DateInput): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    // Textual day extraction — tz-free, and correct for both `YYYY-MM-DD` and
    // any `YYYY-MM-DDT…` form (with or without a trailing `Z` / offset).
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (!match) return '';
    if (import.meta.env?.DEV) warnIfNotMidnight(value);
    return `${match[1]}T00:00:00.000`;
  }
  const d = toDate(value);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000`;
}

/**
 * DEV-only misuse signal for {@link formatLocalDateOnlyForWire}. Fires when a
 * string input carries a time component that isn't midnight, i.e. the caller
 * handed a day-formatter an instant and is losing the time.
 *
 * Deliberately does not inspect `Date` inputs: a `Date` at a non-midnight
 * local time is the NORMAL case for a picker that defaults to "now" on the
 * day the user clicked, so warning there would be noise.
 */
function warnIfNotMidnight(value: string): void {
  const time = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2}):?(\d{2})?/.exec(value);
  if (!time) return; // date-only string — the expected shape
  const [, hh, mm, ss] = time;
  if (hh === '00' && mm === '00' && (ss === undefined || ss === '00')) return;
  console.warn(
    `[date-format] formatLocalDateOnlyForWire received an instant ("${value}") ` +
      `and truncated it to midnight. If you meant to keep the time, use ` +
      `formatLocalDatetimeForWire instead.`,
  );
}

/**
 * Parse a no-Z ISO 8601 datetime as the .NET backend ships it.
 *
 * Examples of the wire format we see in practice:
 *
 *   "2025-01-20T11:33:47.3317152"   // 7-digit fractional seconds (sub-microsecond)
 *   "2025-01-20T11:33:47.331"       // 3-digit fractional seconds (millisecond)
 *   "2025-01-20T11:33:47"            // no fractional seconds
 *
 * Two pitfalls `new Date(str)` falls into directly:
 *
 *   1. **Sub-millisecond precision is non-portable.** Modern V8 (Node,
 *      Chrome) accepts the 7-digit form and truncates. Older Safari and
 *      some embedded runtimes return Invalid Date. We truncate to 3
 *      digits (milliseconds) before parsing so the result is consistent.
 *
 *   2. **No `Z` suffix means local time.** That's what the .NET model
 *      binder writes — it ships timestamps in the org's timezone
 *      (Asia/Dubai for Scrips dev). `new Date()` already interprets
 *      no-Z strings as local time per the ECMAScript spec, so we just
 *      lean on that.
 *
 * Use this for ANY backend timestamp on the .NET surfaces — esp. the
 * `createdOn` / `updatedOn` fields on `BillingProfile`, `Encounter`,
 * etc. NEVER pass these strings directly to `new Date()` from new code
 * — that's the bug class @samer flagged on PR #132 review.
 *
 * Returns `null` for `null` / undefined / `""` inputs and for inputs
 * that fail to parse. Callers should treat null as "show a placeholder."
 */
export function parseNoZIsoDatetime(
  value: string | null | undefined,
): Date | null {
  if (value == null || value === '') return null;
  // Truncate trailing fractional digits past the millisecond (3-digit)
  // boundary. Pattern matches `.dddXXX...` and replaces it with `.ddd`.
  // No-op on strings that already have ≤3 fractional digits or no
  // fractional part at all.
  const truncated = value.replace(/(\.\d{3})\d+$/, '$1');
  const d = new Date(truncated);
  return Number.isNaN(d.getTime()) ? null : d;
}
